import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Mail, Lock, Eye, EyeOff, ChevronRight, Shield, User } from 'lucide-react';
import { Button, Input } from '../../common';
import useAuth from '../../../hooks/useAuth';
import { validateForm } from '../../../utils/validators';
import { ROUTES } from '../../../utils/constants';

const LoginForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();

  const [loginType, setLoginType] = useState('tenant'); // 'admin' ou 'tenant'
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });

  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [generalError, setGeneralError] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    
    // Limpa erro do campo ao digitar
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: '',
      }));
    }
    
    // Limpa erro geral
    if (generalError) {
      setGeneralError('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setGeneralError('');

    // Validação
    const validationRules = {
      email: {
        required: true,
        email: true,
        label: 'Email',
      },
      password: {
        required: true,
        minLength: 6,
        label: 'Senha',
      },
    };

    const validation = validateForm(formData, validationRules);

    if (!validation.valid) {
      setErrors(validation.errors);
      return;
    }

    // Faz login com tipo especificado
    try {
      setLoading(true);
      const result = await login({
        ...formData,
        type: loginType
      });

      if (result.success) {
        // Redireciona para página que tentou acessar ou dashboard
        const from = location.state?.from?.pathname || ROUTES.CONVERSATIONS;
        navigate(from, { replace: true });
      } else {
        setGeneralError(result.error || 'Erro ao fazer login. Verifique suas credenciais.');
      }
    } catch (error) {
      setGeneralError('Erro ao fazer login. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="w-full max-w-md">
      <div className="text-center mb-8">
        {/* Logo */}
        <div className="mb-6">
          <img 
            src="/logo.png" 
            alt="Mara Labs" 
            className="h-16 w-auto mx-auto"
          />
        </div>

        <h1 className="text-3xl font-bold text-gray-900 mb-2 font-display">
          Bem-vindo de volta!
        </h1>
        <p className="text-gray-600">
          Faça login para acessar sua conta
        </p>
      </div>

      {/* Login Type Selector */}
      <div className="mb-6">
        <div className="grid grid-cols-2 gap-3">
          <button
            type="button"
            onClick={() => setLoginType('tenant')}
            className={`flex items-center justify-center gap-2 p-3 rounded-lg border-2 transition-all ${
              loginType === 'tenant'
                ? 'border-primary-500 bg-primary-50 text-primary-700'
                : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
            }`}
          >
            <User className="w-5 h-5" />
            <span className="font-medium">Cliente</span>
          </button>
          
          <button
            type="button"
            onClick={() => setLoginType('admin')}
            className={`flex items-center justify-center gap-2 p-3 rounded-lg border-2 transition-all ${
              loginType === 'admin'
                ? 'border-primary-500 bg-primary-50 text-primary-700'
                : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
            }`}
          >
            <Shield className="w-5 h-5" />
            <span className="font-medium">Admin</span>
          </button>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-5" autoComplete="off">
        {/* Email */}
        <Input
          label="Email"
          type="email"
          name="email"
          value={formData.email}
          onChange={handleChange}
          placeholder="seu@email.com"
          error={errors.email}
          leftIcon={<Mail className="w-5 h-5" />}
          autoComplete="off"
          required
        />

        {/* Password */}
        <Input
          label="Senha"
          type={showPassword ? 'text' : 'password'}
          name="password"
          value={formData.password}
          onChange={handleChange}
          placeholder="••••••••"
          error={errors.password}
          leftIcon={<Lock className="w-5 h-5" />}
          rightIcon={
            showPassword ? (
              <EyeOff className="w-5 h-5" />
            ) : (
              <Eye className="w-5 h-5" />
            )
          }
          onRightIconClick={() => setShowPassword(!showPassword)}
          autoComplete="new-password"
          required
        />

        {/* Remember me & Forgot password */}
        <div className="flex items-center justify-between">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              className="w-4 h-4 rounded border-gray-300 text-primary-500 focus:ring-primary-400"
            />
            <span className="text-sm text-gray-700">Lembrar-me</span>
          </label>

          <a
            href="/recuperar-senha"
            className="text-sm text-primary-600 hover:text-primary-700 font-medium"
          >
            Esqueceu a senha?
          </a>
        </div>

        {/* General Error */}
        {generalError && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg animate-slide-down">
            <p className="text-sm text-red-600">{generalError}</p>
          </div>
        )}

        {/* Submit Button */}
        <Button
          type="submit"
          variant="primary"
          size="lg"
          fullWidth
          loading={loading}
          rightIcon={<ChevronRight className="w-5 h-5" />}
        >
          {loginType === 'admin' ? 'Entrar como Admin' : 'Entrar'}
        </Button>
      </form>

      {/* Help text */}
      <p className="text-center text-sm text-gray-500 mt-8">
        Ao fazer login, você concorda com nossos{' '}
        <a href="/termos" className="text-primary-600 hover:underline">
          Termos de Uso
        </a>{' '}
        e{' '}
        <a href="/privacidade" className="text-primary-600 hover:underline">
          Política de Privacidade
        </a>
      </p>
    </div>
  );
};

export default LoginForm;